import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, FileText, Save } from 'lucide-react';
import { useData } from '../context/DataContext';
import { computeTotals, fmtRM2, fmtDate } from '../lib/store';
import { Button } from './ui/button';
import ShareDocActions from './ShareDocActions';
import { BrandingOverrideSection, ReferenceNumberField } from './BrandingFields';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { toast } from 'sonner';

const CUSTOM = '__custom__';

// Reusable editor for line-item documents (quotations & invoices)
export function LineItemDialog({ open, onOpenChange, initial, onSave, title, extraDates }) {
  const { customers, products, company, addItem } = useData();
  const [form, setForm] = useState(initial);

  useEffect(() => { setForm(initial); }, [initial, open]);

  if (!form) return null;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addLine = () => setForm((f) => ({ ...f, items: [...f.items, { productId: '', name: '', qty: 1, price: 0, taxable: true }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const setLine = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));

  const onSelectProduct = (i, pid) => {
    if (pid === CUSTOM) { setLine(i, { productId: CUSTOM, name: '', price: 0, taxable: true }); return; }
    const p = products.find((x) => x.id === pid);
    if (p) setLine(i, { productId: pid, name: p.name, price: p.price, taxable: p.taxable });
  };

  const saveToCatalog = (i) => {
    const it = form.items[i];
    if (!it.name?.trim()) { toast.error('Enter the item name first'); return; }
    addItem('products', { name: it.name.trim(), description: '', unit: 'unit', price: Number(it.price) || 0, taxable: !!it.taxable });
    toast.success(`"${it.name.trim()}" added to Products / Services`);
  };

  const totals = computeTotals(form.items, company.sstRate);

  const submit = () => {
    if (!form.customerId) { toast.error('Please select a customer'); return; }
    if (!form.items.length || form.items.some((it) => !it.name)) { toast.error('Add at least one valid line item'); return; }
    onSave({ ...form, items: form.items.map((it) => ({ ...it, productId: it.productId === CUSTOM ? '' : it.productId })) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="line-item-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <ReferenceNumberField value={form.number} onChange={(v) => setField('number', v)} />
            </div>
            <div>
              <Label>Customer</Label>
              <Select value={form.customerId} onValueChange={(v) => setField('customerId', v)}>
                <SelectTrigger className="mt-1.5" data-testid="doc-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" className="mt-1.5" value={form.date} onChange={(e) => setField('date', e.target.value)} data-testid="doc-date-input" />
            </div>
            {extraDates && (
              <div>
                <Label>{extraDates.label}</Label>
                <Input type="date" className="mt-1.5" value={form[extraDates.field] || ''} onChange={(e) => setField(extraDates.field, e.target.value)} />
              </div>
            )}
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label>Line Items</Label>
              <Button size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn"><Plus className="mr-1 h-3.5 w-3.5" /> Add Line</Button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, i) => {
                const isCustom = it.productId === CUSTOM || (!it.productId && !!it.name) || it.productId === '';
                return (
                  <div key={i} className="grid grid-cols-12 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2" data-testid={`line-row-${i}`}>
                    <div className="col-span-12 sm:col-span-6">
                      <Select value={products.some((p) => p.id === it.productId) ? it.productId : (it.productId === CUSTOM || it.name ? CUSTOM : '')} onValueChange={(v) => onSelectProduct(i, v)}>
                        <SelectTrigger className="h-9 bg-white" data-testid={`line-product-select-${i}`}><SelectValue placeholder="Select item" /></SelectTrigger>
                        <SelectContent>
                          {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                          <SelectItem value={CUSTOM}>+ New product / service…</SelectItem>
                        </SelectContent>
                      </Select>
                      {isCustom && (
                        <div className="mt-2 flex gap-2">
                          <Input className="h-9 bg-white" placeholder="Name of new product / service" value={it.name}
                            onChange={(e) => setLine(i, { name: e.target.value })} data-testid={`line-custom-name-${i}`} />
                          <Button size="sm" variant="outline" className="h-9 shrink-0" onClick={() => saveToCatalog(i)} data-testid={`line-save-catalog-${i}`}>
                            <Save className="mr-1 h-3.5 w-3.5" /> Save
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="col-span-3 sm:col-span-2"><Input type="number" className="h-9 bg-white" value={it.qty} onChange={(e) => setLine(i, { qty: Number(e.target.value) })} placeholder="Qty" data-testid={`line-qty-${i}`} /></div>
                    <div className="col-span-6 sm:col-span-3"><Input type="number" className="h-9 bg-white" value={it.price} onChange={(e) => setLine(i, { price: Number(e.target.value) })} placeholder="Price" data-testid={`line-price-${i}`} /></div>
                    <div className="col-span-3 flex justify-end sm:col-span-1"><button onClick={() => removeLine(i)} className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`line-remove-${i}`}><Trash2 className="h-4 w-4" /></button></div>
                  </div>
                );
              })}
            </div>
          </div>

          <BrandingOverrideSection form={form} setField={setField} company={company} />

          <div className="ml-auto w-full max-w-xs space-y-1.5 rounded-lg bg-slate-50 p-4 text-sm">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{fmtRM2(totals.subtotal)}</span></div>
            <div className="flex justify-between text-slate-600"><span>SST ({company.sstRate}%)</span><span>{fmtRM2(totals.tax)}</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-1.5 text-base font-bold text-slate-900"><span>Total</span><span>{fmtRM2(totals.total)}</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={submit} data-testid="doc-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Signature + company chop block shared by all document previews
export function SignatureBlock({ doc = {}, company = {}, align = 'left' }) {
  const signature = doc.signatureUrl || company.signatureUrl;
  const chop = doc.chopUrl || company.chopUrl;
  const name = doc.signedBy || company.signedBy;
  const role = doc.signedByTitle || company.signedByTitle;
  if (!signature && !chop && !name) return null;

  return (
    <div className={`mt-8 flex items-end gap-8 ${align === 'center' ? 'justify-center' : ''}`} data-testid="signature-block">
      <div className="min-w-[180px]">
        <div className="relative flex h-16 items-end">
          {signature && <img src={signature} alt="Signature" crossOrigin="anonymous" className="max-h-16 object-contain" />}
        </div>
        <div className="w-48 border-t border-slate-400 pt-1">
          <p className="text-xs font-semibold text-slate-800">{name || 'Authorised Signature'}</p>
          {role && <p className="text-[11px] text-slate-500">{role}</p>}
          <p className="text-[11px] text-slate-400">Date: {fmtDate(doc.date || new Date().toISOString().slice(0, 10))}</p>
        </div>
      </div>
      {chop && (
        <div className="pb-2">
          <img src={chop} alt="Company chop" crossOrigin="anonymous" className="max-h-24 object-contain opacity-90" />
        </div>
      )}
    </div>
  );
}

// Company letterhead block (logo image if uploaded, else the text badge)
export function DocLogo({ doc = {}, company = {}, accentBg }) {
  const logo = doc.logoUrl || company.logoUrl;
  if (logo) return <img src={logo} alt={company.name} crossOrigin="anonymous" className="max-h-14 max-w-[190px] object-contain" data-testid="doc-logo-image" />;
  return (
    <div className={`inline-flex items-center gap-2 rounded-lg ${accentBg} px-3 py-1.5 text-white`}>
      <FileText className="h-4 w-4" /><span className="text-sm font-bold">{company.logoText}</span>
    </div>
  );
}

// Printable document preview
export function DocumentPreview({ open, onOpenChange, doc, type }) {
  const { customers, company } = useData();
  const printRef = useRef(null);
  if (!doc) return null;
  const cust = customers.find((c) => c.id === doc.customerId);
  const totals = computeTotals(doc.items, company.sstRate);
  const labels = { quotation: 'QUOTATION', invoice: 'INVOICE' };
  const accent = type === 'invoice' ? 'text-violet-600' : 'text-blue-600';
  const accentBg = type === 'invoice' ? 'bg-violet-600' : 'bg-blue-600';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-h-[92vh] max-w-2xl overflow-y-auto p-0">
        <DialogHeader className="sr-only"><DialogTitle>{labels[type]} {doc.number}</DialogTitle></DialogHeader>
        <div className="bg-white p-8" ref={printRef} data-testid="document-print-area">
          <div className={`mb-6 flex items-start justify-between border-b-2 pb-6`} style={{ borderColor: '#e2e8f0' }}>
            <div>
              <DocLogo doc={doc} company={company} accentBg={accentBg} />
              <p className="mt-3 text-sm font-semibold text-slate-800">{company.name}</p>
              <p className="max-w-xs text-xs text-slate-500">{company.address}</p>
              <p className="text-xs text-slate-500">{company.phone} · {company.email}</p>
              <p className="text-xs text-slate-500">SST No: {company.sstNo}</p>
            </div>
            <div className="text-right">
              <h2 className={`text-2xl font-extrabold ${accent}`}>{labels[type]}</h2>
              <p className="mt-2 text-sm font-semibold text-slate-800">{doc.number || '—'}</p>
              <p className="text-xs text-slate-500">Date: {fmtDate(doc.date)}</p>
              {doc.validUntil && <p className="text-xs text-slate-500">Valid Until: {fmtDate(doc.validUntil)}</p>}
              {doc.dueDate && <p className="text-xs text-slate-500">Due: {fmtDate(doc.dueDate)}</p>}
            </div>
          </div>

          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Bill To</p>
            <p className="mt-1 font-semibold text-slate-800">{cust?.name}</p>
            <p className="text-xs text-slate-500">{cust?.contact} · {cust?.email}</p>
            <p className="max-w-xs text-xs text-slate-500">{cust?.address}</p>
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-400">
                <th className="py-2">Description</th>
                <th className="py-2 text-center">Qty</th>
                <th className="py-2 text-right">Price</th>
                <th className="py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {doc.items.map((it, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-2.5 text-slate-700">{it.name}{!it.taxable && <span className="ml-1 text-[10px] text-slate-400">(SST exempt)</span>}</td>
                  <td className="py-2.5 text-center text-slate-600">{it.qty}</td>
                  <td className="py-2.5 text-right text-slate-600">{fmtRM2(it.price)}</td>
                  <td className="py-2.5 text-right font-medium text-slate-800">{fmtRM2(it.qty * it.price)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4 flex justify-end">
            <div className="w-full max-w-xs space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{fmtRM2(totals.subtotal)}</span></div>
              <div className="flex justify-between text-slate-600"><span>SST ({company.sstRate}%)</span><span>{fmtRM2(totals.tax)}</span></div>
              <div className={`flex justify-between rounded-lg ${accentBg} px-3 py-2 text-base font-bold text-white`}><span>Total</span><span>{fmtRM2(totals.total)}</span></div>
            </div>
          </div>

          <SignatureBlock doc={doc} company={company} />

          <div className="mt-8 border-t border-slate-200 pt-4">
            <p className="text-xs font-semibold text-slate-500">Terms &amp; Conditions</p>
            <p className="mt-1 text-xs text-slate-400">{company.terms}</p>
            <p className="mt-3 text-xs text-slate-400">Bank: {company.bank} · A/C: {company.bankAccount}</p>
          </div>
        </div>
        <DialogFooter className="border-t border-slate-100 bg-slate-50 px-6 py-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <ShareDocActions
            targetRef={printRef}
            docLabel={type === 'invoice' ? 'Invoice' : 'Quotation'}
            number={doc.number}
            recipientName={cust?.contact || cust?.name}
            recipientEmail={cust?.email}
            recipientPhone={cust?.phone}
            amountText={fmtRM2(totals.total)}
            companyName={company.name}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
